/**
 * 应用管理 API 服务
 */
import { api, API_PATHS, PaginatedData } from './client';

// 应用状态
export type AppStatus = 'pending' | 'approved' | 'rejected';

// 请求类型
export type AppRequestType = 'create' | 'update' | 'delete';

// 已发布应用
export interface AppData {
  id: string;
  orgId: string;
  name: string;
  link: string;
  description: string;
  categoryTags: string[];
  iconUrl: string;
  screenshotUrls: string[];
  creatorId: string;
  creatorEmail: string;
  version: string;
  allowedGroups: string[];
  allowedEmails: string[];
  createdAt: string;
  updatedAt: string;
}

// 应用审核请求
export interface AppRequestData {
  id: string;
  orgId: string;
  appId: string;
  type: AppRequestType;
  status: AppStatus;
  appName: string;
  appLink: string;
  appDescription: string;
  categoryTags: string[];
  iconUrl: string;
  screenshotUrls: string[];
  version: string;
  allowedGroups: string[];
  allowedEmails: string[];
  creatorId: string;
  creatorEmail: string;
  reviewNote: string;
  reviewedBy: string;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// 我的应用响应
export interface MyAppsResponse {
  apps: AppData[];
  pendingRequests: AppRequestData[];
}

// 提交应用请求
export interface SubmitAppRequest {
  name: string;
  link: string;
  description: string;
  categoryTags?: string[];
  iconUrl?: string;
  screenshotUrls?: string[];
  version?: string;
  allowedGroups?: string[];
  allowedEmails?: string[];
}

// 删除应用请求
export interface DeleteAppRequest {
  reason?: string;
}

// 列表查询参数
export interface ListAppsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  [key: string]: string | number | boolean | undefined;
}

// ========== Admin API ==========

/**
 * 获取已发布应用列表（Admin）
 */
export async function getPublishedApps(params?: ListAppsParams): Promise<PaginatedData<AppData>> {
  return api.get<PaginatedData<AppData>>(API_PATHS.APPS, params);
}

/**
 * 获取已发布应用详情（Admin）
 */
export async function getPublishedApp(id: string): Promise<AppData> {
  return api.get<AppData>(`${API_PATHS.APPS}/${id}`);
}

/**
 * 下架应用（Admin）
 */
export async function unpublishApp(id: string): Promise<void> {
  await api.delete<null>(`${API_PATHS.APPS}/${id}`);
}

// ========== Creator API ==========

/**
 * 获取我的应用列表（Creator）
 */
export async function getMyApps(): Promise<MyAppsResponse> {
  return api.get<MyAppsResponse>(API_PATHS.CREATOR_APPS);
}

/**
 * 提交新应用（Creator）
 */
export async function submitApp(data: SubmitAppRequest): Promise<AppRequestData> {
  return api.post<AppRequestData>(API_PATHS.CREATOR_APPS_SUBMIT, data);
}

/**
 * 提交应用更新请求（Creator）
 */
export async function submitAppUpdate(id: string, data: SubmitAppRequest): Promise<AppRequestData> {
  return api.put<AppRequestData>(`${API_PATHS.CREATOR_APPS}/${id}`, data);
}

/**
 * 提交应用删除请求（Creator）
 */
export async function submitAppDelete(id: string, _data?: DeleteAppRequest): Promise<AppRequestData> {
  return api.delete<AppRequestData>(`${API_PATHS.CREATOR_APPS}/${id}`);
}

/**
 * 获取提交历史（Creator）
 */
export async function getSubmitHistory(params?: ListAppsParams): Promise<PaginatedData<AppRequestData>> {
  return api.get<PaginatedData<AppRequestData>>(API_PATHS.CREATOR_HISTORY, params);
}
